import { LEGACY_PATHS, ROUTE_PATHS, normalizePath } from '@/routes.js';

/**
 * Client-side navigation between the four pages.
 *
 * The bundle already holds every route, so a plain `<a href>` throwing the tab
 * through a full document load meant re-fetching the shell, re-parsing the CSS
 * and repainting from white to move between pages it had loaded seconds ago.
 * This intercepts those clicks and swaps the page component instead.
 *
 * It is a click delegate rather than a `<Link>` component on purpose: the links
 * live in the nav, the footer, the hero and the guide rail, and an ordinary
 * anchor is what should appear in the markup anyway — it is what a middle-click,
 * a right-click → open in new tab, and a crawler all need to see.
 */

const ROUTES = new Set(ROUTE_PATHS);
const listeners = new Set();

/** The route a URL resolves to, or null if it is not one of ours. */
function routeFor(url) {
  const path = normalizePath(url.pathname);
  const resolved = LEGACY_PATHS[path] ?? path;
  return ROUTES.has(resolved) ? resolved : null;
}

/**
 * The route an href resolves to, or null when it is not one of ours — used by
 * main.jsx to decide whether hovering a link is worth prefetching a chunk for.
 */
export function routeForHref(href) {
  const url = new URL(href, window.location.href);
  return url.origin === window.location.origin ? routeFor(url) : null;
}

function emit() {
  const path = normalizePath(window.location.pathname);
  listeners.forEach((fn) => fn(path));
}

export function onRouteChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * `html { scroll-behavior: smooth }` would otherwise animate a page swap all
 * the way down the old document — a new page starts at its top, at once.
 */
function jumpToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
}

/**
 * The element a hash points at only exists once the new page has rendered, and
 * three of the four routes are lazy chunks — so look for a few frames before
 * giving up rather than assuming it is there.
 */
function scrollToHash(hash, attempt = 0) {
  const id = decodeURIComponent(hash.slice(1));
  const target = id && document.getElementById(id);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }
  if (attempt < 30) requestAnimationFrame(() => scrollToHash(hash, attempt + 1));
}

export function navigate(to, { replace = false } = {}) {
  const url = new URL(to, window.location.href);
  const route = routeFor(url);
  if (!route) {
    window.location.assign(url.href);
    return;
  }

  const samePage = route === normalizePath(window.location.pathname);
  const href = route + url.search + url.hash;
  if (href !== window.location.pathname + window.location.search + window.location.hash) {
    window.history[replace ? 'replaceState' : 'pushState']({}, '', href);
  }

  if (!samePage) {
    emit();
    jumpToTop();
  }
  if (url.hash) scrollToHash(url.hash);
  else if (samePage) jumpToTop();
}

/** Clicks the browser must keep: new tab, download, a different origin. */
function isPlainLeftClick(event) {
  return (
    event.button === 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

function onClick(event) {
  if (!isPlainLeftClick(event)) return;

  const anchor = event.target.closest?.('a[href]');
  if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
  if (anchor.getAttribute('rel')?.includes('external')) return;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return;

  const route = routeFor(url);
  // Not a route: a PDF under /guides, or anything else the server owns. Let it
  // load normally — this must never swallow a link the bundle cannot render.
  if (!route) return;

  // A hash on the page we are already on is the browser's job: it scrolls
  // smoothly and writes the history entry itself.
  const samePage = route === normalizePath(window.location.pathname);
  if (samePage && url.hash) return;

  event.preventDefault();
  navigate(url.href);
}

/**
 * Back/forward. The page swap is asynchronous (three routes are lazy chunks),
 * so a hash the browser would normally have restored has to be re-applied once
 * the target exists.
 */
function onPopState() {
  emit();
  if (window.location.hash) scrollToHash(window.location.hash);
}

export function startRouter() {
  document.addEventListener('click', onClick);
  window.addEventListener('popstate', onPopState);
}
